import React, { useEffect, useMemo, useCallback, useRef, useState } from "react";
import { useAppSelector, useAppDispatch } from "hooks/redux";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { useHistory, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import axios, { CancelToken, CancelTokenSource } from "axios";

import {
  GetTeeSheetNotificationsByDate,
  GetTeeTimeNew,
  GetTeeTimeBookingCategory,
  GetAllTeeTimeCartsAvailable,
  ITeeSheet,
  GetTeeSheets,
  ITeeTimeCartsAvailable,
} from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import { StatusCode } from "api/protocols";

import { DESKTOP_WIDTH, MOBILE_WIDTH } from "helpers/ScreenSizes";
import { formatDate } from "helpers/Helpers";

import { selectDate } from "redux/actions/teesheet";
import { showError } from "redux/actions/ui";
import { loadFacilityWeather } from "redux/actions/facility";

import NewStatisticsBar from "./elements/NewStatisticsBar";
import TeesheetDateControlsRow from "elements/teesheet/TeesheetDateControlsRow";
import NewTeeSheetTabs from "./NewTeeSheetTabs";
import Callout from "components/callout/Callout";
import Spin from "components/spin/spin";
import teeSheetToast from "components/teeSheetToast/TeeSheetToast";

import { INotification } from "api/rpc/2024-04/customer/teesheet";
import { ICourse, IDivision } from "redux/reducers/models/facility";
import { ITeeTimeNew, ITeeTimeBookingCategory, ITeeTimeSlotNew } from "redux/reducers/models/teetime";

import "../styles.scss";
import { cloneDeep, Dictionary, flatten, groupBy, uniqBy } from "lodash";
import {
  loadingTeetimeTabActionsAreEqual,
  TLoadingTeetimeTabsActionStatus,
  TNewTeeSheetLoadingTeetimeTabAction,
} from "./loadingTeetimeTabActions";
import { useTeeSheetSideBySideSettings } from "hooks/useTeeSheetSideBySideSettings/useTeeSheetSideBySideSettings";
import TeeSheetOverview from "./elements/TeeSheetOverview";
import { TeeSheetLocationState, resolveTeeSheetMountDate } from "elements/teesheet/teeSheetNavigationState";

interface INewTeeSheetState {
  notifications: INotification[];
  currentCourseId: number;
  currentDivisions: IDivision[];
  currentDivisionId: number;
  debouncedTeeTimeSearch: string;
  jumpToStringTime: string;
}

export interface ITeeTimeTabNew extends ITeeTimeNew {
  power_cart_quantity?: number;
  carts_available?: number;
  power_cart_numbers?: string;
}

function normalizeTeeTimeTabs(data: unknown): ITeeTimeTabNew[] | null {
  const list = Array.isArray(data)
    ? data
    : data && typeof data === "object"
      ? Object.values(data as Record<string, unknown>)
      : null;

  if (!list) {
    return null;
  }

  const tabs = list.filter(
    (item): item is ITeeTimeTabNew =>
      !!item && typeof item === "object" && "start_time" in item && "slots" in item,
  );

  if (tabs.length === 0 || tabs.length !== list.length) {
    return Array.isArray(data) ? (data as ITeeTimeTabNew[]) : null;
  }

  return tabs.map(tab => ({
    ...tab,
    slots: Array.isArray(tab.slots) ? tab.slots : Object.values(tab.slots ?? {}),
  }));
}

export interface ISideBySideTeeSheetDivision {
  id: number;
  title: string;
  division: IDivision;
  course: ICourse;
  teetimeTabs: ITeeTimeTabNew[];
}

const COMBINED_DIVISION_ID = -1;

export default function NewTeeSheet() {
  const windowSize = useWindowSize();
  const { teeSheetStore, facilityStore } = useAppSelector(store => store);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation<TeeSheetLocationState>();
  const { t, i18n } = useTranslation();

  const urlParams = new URLSearchParams(window.location.search);

  const { sideBySideSettings, teeSheetView, loadSideBySideSettings, updateSideBySideSettings } =
    useTeeSheetSideBySideSettings();

  const sideBySideSettingsRef = useRef(sideBySideSettings);
  sideBySideSettingsRef.current = sideBySideSettings;

  const sideBySideSelectedDivisionIdsSetKey = useMemo(() => {
    const ids = sideBySideSettings?.selectedDivisionIds ?? [];
    return ids
      .slice()
      .sort((a, b) => a - b)
      .join(",");
  }, [sideBySideSettings?.selectedDivisionIds]);

  const getSideBySideSettingsKeyPrefix = (facilityShortName: string) => `facility_admin_${facilityShortName}`;

  const sideBySideSettingsKeyPrefix = getSideBySideSettingsKeyPrefix(facilityStore.facility?.short_name);

  const [teeSheet, setTeeSheet] = useState<ITeeSheet>(undefined);
  const [teetimeTabs, setTeetimeTabs] = useState<ITeeTimeTabNew[]>(undefined);

  const [teeTimeBookingCategories, setTeeTimeBookingCategories] = useState<ITeeTimeBookingCategory[][]>(undefined);

  const [loadingTeetimeTabActions, setLoadingTeetimeTabActions] = useState<TNewTeeSheetLoadingTeetimeTabAction[]>([]);
  const [bookingDragActive, setBookingDragActive] = useState(false);
  const [teeSheetLoaded, setTeeSheetLoaded] = useState<boolean>(false);

  const [state, setState] = useState<INewTeeSheetState>({
    notifications: null,
    currentCourseId: null,
    currentDivisions: null,
    currentDivisionId: null,
    debouncedTeeTimeSearch: "",
    jumpToStringTime: "",
  });

  const autoScrollNavigationKeyPrefix = useMemo(() => {
    const dateStr = teeSheetStore.selectedDate ? formatDate(teeSheetStore.selectedDate as Date) : "";
    return [dateStr, state.currentCourseId ?? "", state.currentDivisionId ?? "", state.jumpToStringTime ?? ""].join(
      "|",
    );
  }, [teeSheetStore.selectedDate, state.currentCourseId, state.currentDivisionId, state.jumpToStringTime]);

  const loadingTeeTimeTabsAllowed =
    teeSheetStore.selectedDate &&
    facilityStore.teeSheetSettings?.validate_power_cart_count != null &&
    (teeSheetView !== "default" || (state.currentCourseId && state.currentDivisionId && state.currentDivisions));

  const allDivisions: IDivision[] = useMemo(() => {
    if (facilityStore.facility?.courses?.length > 0) {
      return flatten(facilityStore.facility.courses.map(course => course.divisions ?? []));
    } else {
      return [];
    }
  }, [facilityStore.facility?.courses]);

  const sideBySideTeeSheetDivisions: ISideBySideTeeSheetDivision[] = useMemo(() => {
    if (
      teeSheetView === "side-by-side" &&
      teetimeTabs &&
      facilityStore.facility?.courses?.length > 0 &&
      allDivisions?.length > 0
    ) {
      const updatedSideBySideTeeSheetDivisions: ISideBySideTeeSheetDivision[] = [];

      const sideBySideTeeTimeTabGroups: Dictionary<ITeeTimeTabNew[]> = groupBy(
        teetimeTabs,
        teetimeTab => teetimeTab?.slots[0]?.division_id,
      );

      const sideBySideDivisions = allDivisions.filter(division =>
        sideBySideSettings.selectedDivisionIds.includes(division.id),
      );

      for (const divisionId of sideBySideSettings.selectedDivisionIds) {
        const divisionIdKey = String(divisionId);
        const tabsForDivision = sideBySideTeeTimeTabGroups[divisionIdKey];
        if (!tabsForDivision) {
          continue;
        }

        const foundDivision = sideBySideDivisions.find(division => division.id === divisionId);

        if (foundDivision) {
          const foundCourse = facilityStore.facility.courses.find(course => course.id === foundDivision.course_id);

          if (foundCourse) {
            updatedSideBySideTeeSheetDivisions.push({
              id: foundDivision.id,
              division: { ...foundDivision },
              course: { ...foundCourse },
              title: `${foundCourse.full_name}: ${foundDivision.title}`,
              teetimeTabs: tabsForDivision,
            });
          }
        }
      }

      return updatedSideBySideTeeSheetDivisions;
    } else {
      return undefined;
    }
  }, [teeSheetView, teetimeTabs, facilityStore.facility?.courses, allDivisions, sideBySideSettings]);

  const teeSheetExitCancelSource = useRef<CancelTokenSource>(axios.CancelToken.source());

  useEffect(() => {
    teeSheetExitCancelSource.current = axios.CancelToken.source();

    const resolvedDate = resolveTeeSheetMountDate({
      urlDate: urlParams.get("date"),
      params: location.state?.params,
      existingSelectedDate: teeSheetStore.selectedDate as Date,
    });

    if (resolvedDate) {
      dispatch(selectDate(resolvedDate));
    }

    return () => teeSheetExitCancelSource.current.cancel();
  }, []);

  useEffect(() => {
    if (facilityStore.facility) {
      void dispatch(loadFacilityWeather(facilityStore.facility.id, true));

      loadSideBySideSettings(getSideBySideSettingsKeyPrefix(facilityStore.facility.short_name));

      const currentDivision = parseInt(
        localStorage.getItem(`${facilityStore.facility.short_name}_tee_sheet_current_division`),
      );
      const currentCourseId = parseInt(
        localStorage.getItem(`${facilityStore.facility.short_name}_tee_sheet_current_course_id`),
      );
      const currentDivisions = facilityStore.facility.courses.find(course => course.id === currentCourseId)?.divisions;

      const localStorageDataIsValid = currentDivision && currentCourseId && currentDivisions;

      setState(prevState => ({
        ...prevState,
        currentDivisionId: localStorageDataIsValid
          ? currentDivision
          : facilityStore.facility.courses[0]?.divisions[0]?.id,
        currentDivisions: localStorageDataIsValid ? currentDivisions : facilityStore.facility.courses[0].divisions,
        currentCourseId: localStorageDataIsValid ? currentCourseId : facilityStore.facility.courses[0].id,
      }));
    }
  }, [facilityStore.facility, i18n.language]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTeeSheetNotifications(teeSheetStore.selectedDate as Date, source.token);
    return () => source.cancel();
  }, [teeSheetStore.selectedDate]);

  function populateTeetimeTabWithCartsAvailable(
    teetimeTab: ITeeTimeTabNew,
    cartsAvailableList: ITeeTimeCartsAvailable[],
  ) {
    const cartsAvailable = cartsAvailableList.find(
      cartsAvailable => cartsAvailable.id === teetimeTab.slots[0].tee_time_id,
    );

    teetimeTab.carts_available = cartsAvailable?.carts_available ?? 0;
    teetimeTab.power_cart_quantity = calculatePowerCartQuantity(teetimeTab);
    teetimeTab.power_cart_numbers = getPowerCartNumbers(teetimeTab.slots);
  }

  function populateTeetimeTabsWithCartsAvailable(
    teetimeTabsToChange: ITeeTimeTabNew[],
    cartsAvailableList: ITeeTimeCartsAvailable[],
  ) {
    teetimeTabsToChange?.forEach(teetimeTab => populateTeetimeTabWithCartsAvailable(teetimeTab, cartsAvailableList));
  }

  const loadTeeTimeTabs = useCallback(
    async (token: CancelToken) => {
      setTeetimeTabs(undefined);

      const formattedDate = formatDate(teeSheetStore.selectedDate as Date);

      if (teeSheetView !== "default") {
        const getTeeTimesResponse = await GetTeeTimeNew(
          {
            date: formattedDate,
            division_ids: sideBySideSettingsRef.current.selectedDivisionIds,
          },
          token ? false : true,
          token,
        );

        if (token?.reason) {
          return;
        }

        if (getTeeTimesResponse.status !== StatusCode.OK) {
          dispatch(showError(t("Error loading tee times")));
          return;
        }

        const teetimes = normalizeTeeTimeTabs(getTeeTimesResponse.data);

        if (teetimes) {
          setTeetimeTabs(teetimes);
        } else {
          dispatch(showError(t("Error loading tee times")));
        }
      } else {
        let division_id: number = undefined;
        let division_ids: number[] = undefined;

        if (state.currentDivisionId != COMBINED_DIVISION_ID) {
          division_id = state.currentDivisionId;
        } else {
          division_ids = state.currentDivisions.map(division => division.id);
        }

        const teetimesRes = await GetTeeTimeNew(
          {
            date: formattedDate,
            course_id: state.currentCourseId,
            division_id,
            division_ids,
          },
          token ? false : true,
          token,
        );

        if (token?.reason) {
          return;
        }

        if (teetimesRes.status !== StatusCode.OK) {
          dispatch(showError(t("Error loading tee times")));
          return;
        }

        const updatedTeetimeTabs = normalizeTeeTimeTabs(teetimesRes.data);

        if (!updatedTeetimeTabs) {
          dispatch(showError(t("Error loading tee times")));
          return;
        }

        if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
          const cartsAvailableRes = await GetAllTeeTimeCartsAvailable(
            {
              date: formattedDate,
              course_id: state.currentCourseId,
            },
            token ? false : true,
            token,
          );

          if (token?.reason) {
            return;
          }

          if (cartsAvailableRes.status !== StatusCode.OK) {
            dispatch(showError(t("Unable to retrieve cart availability")));
            return;
          }

          populateTeetimeTabsWithCartsAvailable(updatedTeetimeTabs, cartsAvailableRes.data);
        }

        if (updatedTeetimeTabs) {
          setTeetimeTabs(updatedTeetimeTabs);
        }
      }
    },
    [
      teeSheetStore.selectedDate,
      facilityStore.teeSheetSettings?.validate_power_cart_count,
      teeSheetView,
      sideBySideSelectedDivisionIdsSetKey,
      state.currentCourseId,
      state.currentDivisionId,
      state.currentDivisions,
    ],
  );

  // Update teetimes on course, division, or date change. Or according to side by side settings changes.
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (loadingTeeTimeTabsAllowed && teeSheetStore.selectedDate) {
      void loadTeeTimeTabs(source.token);
      void loadTeeSheet(teeSheetStore.selectedDate as Date, source.token);
      void loadTeeTimeBookingCategories(teeSheetStore.selectedDate as Date, source.token);
    }

    return () => source.cancel();
  }, [loadingTeeTimeTabsAllowed, teeSheetStore.selectedDate, loadTeeTimeTabs]);

  // Save course and divison ids in local storage when they are updated
  useEffect(() => {
    if (state.currentCourseId && state.currentDivisionId && facilityStore.facility) {
      localStorage.setItem(
        `${facilityStore.facility.short_name}_tee_sheet_current_division`,
        String(state.currentDivisionId),
      );
      localStorage.setItem(
        `${facilityStore.facility.short_name}_tee_sheet_current_course_id`,
        String(state.currentCourseId),
      );
    }
  }, [state.currentCourseId, state.currentDivisionId]);

  function handleCourseChange(courseId: number) {
    let currentCourse: ICourse = undefined;
    if (facilityStore?.facility?.courses?.length > 0) {
      currentCourse = facilityStore.facility.courses.find(course => course.id === courseId);
    }

    if (currentCourse === undefined) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      currentCourseId: currentCourse.id,
      currentDivisions: currentCourse.divisions,
      currentDivisionId: currentCourse.divisions[0]?.id,
    }));
  }

  function getPowerCartNumbers(slots: ITeeTimeSlotNew[]): string {
    const powerCartNumbers = slots.reduce((accumulator: number[], currentSlot) => {
      if (currentSlot.inventory_power_cart_number != null) {
        return [...accumulator, currentSlot.inventory_power_cart_number];
      } else if (currentSlot.power_cart_number != null) {
        return [...accumulator, currentSlot.power_cart_number];
      } else {
        return accumulator;
      }
    }, []);

    return powerCartNumbers.join("/");
  }

  function calculatePowerCartQuantity(teetime: ITeeTimeTabNew) {
    if (teetime.slots.some(slot => slot.turn === 1)) {
      return 0;
    }

    const uniqueBookedSlots = uniqBy(teetime.slots, slot => slot.tee_time_booking_id);
    const powerCartQuantity = uniqueBookedSlots.reduce(
      (accumulator, currentSlot) => accumulator + (currentSlot.power_cart_quantity ?? 0),
      0,
    );

    return powerCartQuantity;
  }

  async function loadTeeSheet(selectedDate: Date, token: CancelToken) {
    const teeSheetsRes = await GetTeeSheets(
      {
        date: formatDate(selectedDate),
        extended_carts_available: true,
      },
      false,
      token,
    );

    if (token?.reason) {
      return;
    }

    if (teeSheetsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading tee sheet information")));
      return;
    }

    setTeeSheet(teeSheetsRes.data?.[0]);
    setTeeSheetLoaded(true);
  }

  async function loadActionTeeTimeTabs(
    loadingTeetimeTabAction: TNewTeeSheetLoadingTeetimeTabAction,
    useLoader: boolean,
  ): Promise<TLoadingTeetimeTabsActionStatus> {
    if (
      (teeSheetView === "default" && (!state.currentCourseId || !state.currentDivisionId || !state.currentDivisions)) ||
      facilityStore.teeSheetSettings?.validate_power_cart_count == null
    ) {
      dispatch(showError(t("Error loading tee times")));
      return "failure";
    }

    try {
      if (useLoader) {
        setLoadingTeetimeTabActions(prev => {
          return [...prev, loadingTeetimeTabAction];
        });
      }

      const loadingTeeTimeIds =
        loadingTeetimeTabAction.type === "move-bookings" || loadingTeetimeTabAction.type === "add-bookings"
          ? loadingTeetimeTabAction.ids
          : [loadingTeetimeTabAction.id];

      const formattedDate = formatDate(teeSheetStore.selectedDate as Date);

      let teetimes: ITeeTimeNew[] = null;
      let cartsAvailable: ITeeTimeCartsAvailable[] = [];

      if (teeSheetView !== "default") {
        const teetimesRes = await GetTeeTimeNew(
          {
            ids: loadingTeeTimeIds,
            date: formattedDate,
            division_ids: sideBySideSettings.selectedDivisionIds,
          },
          false,
          teeSheetExitCancelSource.current.token,
        );

        if (teeSheetExitCancelSource.current.token?.reason) {
          return "failure";
        }

        if (teetimesRes.status !== StatusCode.OK) {
          dispatch(showError(t("Error loading tee times")));
          return "failure";
        }

        teetimes = normalizeTeeTimeTabs(teetimesRes.data);
      } else {
        let division_id: number = undefined;
        let division_ids: number[] = undefined;

        if (state.currentDivisionId != COMBINED_DIVISION_ID) {
          division_id = state.currentDivisionId;
        } else {
          division_ids = state.currentDivisions.map(division => division.id);
        }

        const teetimesRes = await GetTeeTimeNew(
          {
            ids: loadingTeeTimeIds,
            date: formattedDate,
            course_id: state.currentCourseId,
            division_id,
            division_ids,
          },
          false,
          teeSheetExitCancelSource.current.token,
        );

        if (teeSheetExitCancelSource.current.token?.reason) {
          return "failure";
        }

        if (teetimesRes.status !== StatusCode.OK) {
          dispatch(showError(t("Error loading tee times")));
          return "failure";
        }

        if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
          const cartsAvailableRes = await GetAllTeeTimeCartsAvailable(
            {
              date: formattedDate,
              course_id: state.currentCourseId,
            },
            false,
            teeSheetExitCancelSource.current.token,
          );

          if (teeSheetExitCancelSource.current.token?.reason) {
            return "failure";
          }

          if (cartsAvailableRes.status !== StatusCode.OK) {
            dispatch(showError(t("Unable to retrieve cart availability")));
            return "failure";
          }

          cartsAvailable = cartsAvailableRes.data;
        }

        teetimes = normalizeTeeTimeTabs(teetimesRes.data);
      }

      if (!teetimes) {
        return "failure";
      } else {
        setTeetimeTabs(prevTeetimeTabs => {
          if (prevTeetimeTabs == null) {
            return prevTeetimeTabs;
          } else {
            return prevTeetimeTabs.map(prevTeetimeTab => {
              const specificTeetime = teetimes.find(
                teetime => teetime.slots[0].tee_time_id === prevTeetimeTab.slots[0].tee_time_id,
              );

              const updatedTeetimeTab: ITeeTimeTabNew = specificTeetime ? specificTeetime : cloneDeep(prevTeetimeTab);

              if (teeSheetView === "default" && facilityStore.teeSheetSettings?.validate_power_cart_count) {
                populateTeetimeTabWithCartsAvailable(updatedTeetimeTab, cartsAvailable);
              }

              return updatedTeetimeTab;
            });
          }
        });

        return "success";
      }
    } finally {
      if (useLoader) {
        setLoadingTeetimeTabActions(prev =>
          prev.filter(
            prevLoadingTeetimeTabAction =>
              !loadingTeetimeTabActionsAreEqual(prevLoadingTeetimeTabAction, loadingTeetimeTabAction),
          ),
        );
      }
    }
  }

  async function loadTeeTimeBookingCategories(selectedDate: Date, token?: CancelToken) {
    const formattedDate = formatDate(selectedDate);

    const categoryRes = await GetTeeTimeBookingCategory({ date: formattedDate }, false, token);

    if (token?.reason) {
      return;
    }

    if (categoryRes.status !== StatusCode.OK) {
      return;
    }

    setTeeTimeBookingCategories(categoryRes.data as ITeeTimeBookingCategory[][]);
  }

  async function loadTeeSheetNotifications(date: Date, token?: CancelToken) {
    teeSheetToast.show([]);

    const formattedDate: string = formatDate(date);
    const notificationRes = await GetTeeSheetNotificationsByDate(
      { date: formattedDate, user_type: "admin" },
      false,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (notificationRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading tee sheet notifications")));
      return;
    }

    if (notificationRes.status === StatusCode.OK && notificationRes.data?.length !== 0) {
      const toastList: Array<{
        subject: string;
        content: string;
        duration?: number;
        onClose?: () => void;
      }> = notificationRes.data?.map((data: any) => {
        let duration = 10000;
        if (data.type !== "passive") {
          duration = null;
        }
        return {
          subject: data.subject,
          content: data.content,
          duration: duration,
        };
      });
      teeSheetToast.show(toastList);
    }

    setState(prevState => ({ ...prevState, notifications: notificationRes.data }));
  }

  function changeTeeSheetDate(selectedDate: Date) {
    const currentDate = teeSheetStore.selectedDate as Date;
    const isSameDay = currentDate && formatDate(currentDate) === formatDate(selectedDate);
    if (isSameDay) {
      setTeeSheetLoaded(false);
      setTeetimeTabs(undefined);
      reloadTeeTimesAndSheet();
      return;
    }
    setTeeSheetLoaded(false);
    setTeetimeTabs(undefined);
    dispatch(selectDate(selectedDate));
  }

  function updateTeeTimes(teeTimes: Array<ITeeTimeNew>) {
    setTeetimeTabs(teeTimes);
  }

  function reloadTeeTimesAndSheet() {
    if (loadingTeeTimeTabsAllowed && teeSheetStore.selectedDate) {
      void loadTeeTimeTabs(teeSheetExitCancelSource.current.token);
      void loadTeeSheet(teeSheetStore.selectedDate as Date, teeSheetExitCancelSource.current.token);
    }
  }

  function renderTeeSheetTabs() {
    const spinner = (
      <div style={{ height: "24px", overflow: "hidden" }}>
        <Spin />
      </div>
    );

    const callout = (
      <Callout
        type="error"
        title={t("No Tee Times")}
        content={t("Looks like there is no template set up for the selected date. Would you like to apply one now?")}
        actions={{
          primary: {
            label: t("Go to Calendar"),
            onClick: () => history.push(`/admin/settings/tee-sheet/calendar`),
          },
        }}
      />
    );

    if (teeSheetView === "side-by-side") {
      if (!sideBySideTeeSheetDivisions) {
        return spinner;
      } else if (sideBySideTeeSheetDivisions.length === 0) {
        return callout;
      }
    } else {
      if (!teetimeTabs) {
        return spinner;
      } else if (teetimeTabs.length === 0) {
        return callout;
      }
    }

    return (
      <NewTeeSheetTabs
        teeSheetView={teeSheetView}
        sideBySideTeeSheetDivisions={sideBySideTeeSheetDivisions}
        allDivisions={allDivisions}
        teetimeTabs={teetimeTabs}
        sideBySideSettings={sideBySideSettings}
        updateSideBySideSettings={updateSideBySideSettings}
        sideBySideSettingsKeyPrefix={sideBySideSettingsKeyPrefix}
        teetimeBookingCategories={teeTimeBookingCategories}
        reloadTeeTimeTabs={() => {
          if (loadingTeeTimeTabsAllowed) {
            void loadTeeTimeTabs(teeSheetExitCancelSource.current.token);
          }
        }}
        reloadActionTeeTimeTabs={loadActionTeeTimeTabs}
        setTeeTimes={teeTimes => updateTeeTimes(teeTimes)}
        debouncedTeeTimeSearch={state.debouncedTeeTimeSearch}
        loadingTeetimeTabActions={loadingTeetimeTabActions}
        setLoadingTeetimeTabActions={setLoadingTeetimeTabActions}
        jumpToTime={state.jumpToStringTime}
        autoScrollNavigationKeyPrefix={autoScrollNavigationKeyPrefix}
        onBookingDragActiveChange={setBookingDragActive}
        preferredDivisionId={
          state.currentDivisionId != null && state.currentDivisionId > 0 ? state.currentDivisionId : undefined
        }
      />
    );
  }

  return (
    <>
      <div className="flex flex-col">
        <div className="flex flex-col flex-shrink-0">
          {windowSize.width > MOBILE_WIDTH && (
            <>
              <div className="teesheet-header-shell">
                <TeesheetDateControlsRow
                  selectedDate={teeSheetStore.selectedDate as Date}
                  onDateChange={changeTeeSheetDate}
                  weather={facilityStore.weather}
                  windowWidth={windowSize.width}
                  bookingDragActive={bookingDragActive}
                />
              </div>

              <div className=" bg-white border-b-[0.0625rem] w-full border-[#EAECF0]"></div>
            </>
          )}

          <NewStatisticsBar
            teeSheet={teeSheet}
            teeTimes={teetimeTabs}
            reloadTeeTimesAndSheet={reloadTeeTimesAndSheet}
            sideBySideSettings={sideBySideSettings}
            updateSideBySideSettings={updateSideBySideSettings}
            sideBySideSettingsKeyPrefix={sideBySideSettingsKeyPrefix}
            teeSheetLoaded={teeSheetLoaded}
            setDebouncedTeeTimeSearch={teeTimeSearch =>
              setState(prevState => ({ ...prevState, debouncedTeeTimeSearch: teeTimeSearch }))
            }
            currentCourseId={state.currentCourseId}
            currentDivisionId={state.currentDivisionId}
            currentDivisions={state.currentDivisions}
            onCourseChange={handleCourseChange}
            onDivisionChange={divisionId => setState(prevState => ({ ...prevState, currentDivisionId: divisionId }))}
            filtersDisabled={sideBySideSettings?.enabled}
            combinedDivisionId={COMBINED_DIVISION_ID}
            selectedDate={teeSheetStore.selectedDate as Date}
            onDateChange={changeTeeSheetDate}
            windowWidth={windowSize.width}
          />
        </div>

        <div className="facility-teesheet-content-row flex flex-1 min-h-0 overflow-hidden">
          {facilityStore.teeSheetSettings?.tee_sheet_overview && teetimeTabs && windowSize.width >= DESKTOP_WIDTH ? (
            <TeeSheetOverview
              teetimes={teetimeTabs}
              selectTime={(time: string) => {
                setState(prev => ({ ...prev, jumpToStringTime: time }));
                console.log("time", time);
              }}
              allDivisions={allDivisions}
            />
          ) : null}
          <div className="content-container facility-new-teesheet-layout flex-1 min-h-0 min-w-0">
            <div className="facility-new-teesheet-tabs-host">{renderTeeSheetTabs()}</div>
          </div>
        </div>
      </div>
    </>
  );
}
